import { Api, Endpoint } from '@square/ignition';
import { ExternalDocs } from '@square/ignition/dist/types/types/external-docs';
import { ReferenceHrefBuilders, toUrl } from '@squareup/dex-tech-utils-routing';
import { ChildrenProp } from '@squareup/dex-types-shared-ui';
import { Link } from '@squareup/dex-ui';
import { useIsMarketComponentLoaded } from '@squareup/dex-ui-hooks';
import { Box, Paragraph20 } from '@squareup/dex-ui-shared-base';
import { MarketButton } from '@squareup/dex-ui-shared-market';
import {
  convertToEnvironmentUrl,
  getEnvironmentName,
} from '@squareup/dex-utils-environment';
import { breakHtmlForCamelCaseReact } from '@squareup/dex-utils-text';
import clsx from 'clsx';
import React, { FunctionComponent, useRef } from 'react';

import { ReferenceGuide, ReferenceGuideProps } from '../ReferenceGuide';

import styles from './reference-info-box.module.css';

export interface ReferenceInfoBoxProps {
  permissions?: string[];
  externalDocs?: ExternalDocs | undefined;
  explorerButtonModel?: boolean;
  deprecationDate?: string | undefined;
  retirementDate?: string | undefined;
  replacementEndpoint?: Endpoint | undefined;
  migrationGuideUrl?: string | undefined;
  children?: string;
  api?: Api | undefined;
  endpoint?: Endpoint;
  /**
   * If links are generated from other meta data will use this
   * version in creating the href.
   */
  versionInfo?: string;
}

interface ReferenceInfoBoxButtonItemProps {
  trackingId: string;
  href: React.ComponentProps<typeof Link>['href'] | undefined;
  rank: React.ComponentProps<typeof MarketButton>['rank'];
}

const ReferenceInfoBoxItem: FunctionComponent<ReferenceGuideProps> = (
  props
) => {
  return (
    <Box margin={{ right: '4x' }}>
      <ReferenceGuide {...props} />
    </Box>
  );
};

const ReferenceInfoBoxButtonItem: FunctionComponent<
  ReferenceInfoBoxButtonItemProps & ChildrenProp
> = ({ children, trackingId, href, rank }) => {
  const ref = useRef<globalThis.HTMLMarketButtonElement>(null);
  const [isMarketComponentLoaded, onMarketRef] = useIsMarketComponentLoaded(
    ref,
    'market-button'
  );

  return (
    <Box
      margin={{ bottom: '3x' }}
      className={clsx(
        styles['info-box-button-col'],
        !isMarketComponentLoaded && styles['hidden']
      )}
    >
      <Box>
        <Link passHref omitAnchor={true} trackingId={trackingId} href={href}>
          <MarketButton
            trackingId={trackingId}
            /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
            rank={rank!}
            size="small"
            ref={onMarketRef}
          >
            {children}
          </MarketButton>
        </Link>
      </Box>
    </Box>
  );
};

const ReferenceInfoBox: FunctionComponent<ReferenceInfoBoxProps> = ({
  permissions = [],
  externalDocs,
  explorerButtonModel,
  deprecationDate,
  retirementDate,
  api,
  endpoint,
  versionInfo,
  replacementEndpoint,
  migrationGuideUrl,
}) => {
  const apiPath = `${api?.id}-api`;
  const endpointId = endpoint?.id || '';
  const replacementApiPath = `${replacementEndpoint?.api?.id}-api`;
  const replacementEndpointId = replacementEndpoint?.id || '';
  const permissionsList =
    permissions.length > 0 ? (
      <Paragraph20 colorVariant={'10'} as={'span'}>
        Permissions:
        <Paragraph20 as={'span'} weight={'semi-bold'} margin={{ left: '0.5x' }}>
          {permissions.join(', ')}
        </Paragraph20>
      </Paragraph20>
    ) : null;

  return (
    <Box margin={{ vertical: '2x' }}>
      {permissionsList}
      <Box
        className={styles['ref-info-box']}
        border={{ line: { top: 'standard' } }}
        margin={{ top: '4x' }}
        padding={{ top: '3x' }}
      >
        {externalDocs && (
          <ReferenceInfoBoxItem
            title={'Guide'}
            content={externalDocs.description}
            linkProps={{
              href: externalDocs.url,
              trackingId: 'endpoint-guide-url',
            }}
          />
        )}

        {deprecationDate && (
          <ReferenceInfoBoxItem
            title={'Deprecation date'}
            content={deprecationDate}
          />
        )}

        {retirementDate && (
          <ReferenceInfoBoxItem
            title={'Retirement date'}
            content={retirementDate}
          />
        )}
        {replacementEndpoint && versionInfo && (
          <ReferenceInfoBoxItem
            title={'Replaced by'}
            content={breakHtmlForCamelCaseReact(replacementEndpoint.name)}
            linkProps={{
              href: toUrl(
                ReferenceHrefBuilders.versionApiEndpoint({
                  api: replacementApiPath,
                  endpoint: replacementEndpointId,
                  square_version: versionInfo,
                })
              ),
              trackingId: 'endpoint-replaced-by-link',
            }}
          />
        )}
        {explorerButtonModel && versionInfo && (
          <ReferenceInfoBoxButtonItem
            href={ReferenceHrefBuilders.versionSquareExplorerEndpoint({
              api: apiPath,
              endpoint: endpointId,
              square_version: versionInfo,
            })}
            trackingId={'navigate-to-try-api-explorer'}
            rank="primary"
          >
            Try in API Explorer
          </ReferenceInfoBoxButtonItem>
        )}
        {migrationGuideUrl && (
          <ReferenceInfoBoxButtonItem
            href={convertToEnvironmentUrl(
              migrationGuideUrl,
              getEnvironmentName()
            )}
            trackingId={'navigate-to-migration-url'}
            rank="secondary"
          >
            Migration guide
          </ReferenceInfoBoxButtonItem>
        )}
      </Box>
    </Box>
  );
};

export { ReferenceInfoBox };
